import { z } from 'zod';

export const TierRevisionSchema = z.string().regex(/^[1-9]\d*$/).refine(value => BigInt(value) <= 18446744073709551615n, '版本超出範圍');
const state = z.enum(['PENDING', 'APPLYING', 'APPLIED', 'FAILED']);
export const TierPolicyUpdateSchema = z.object({
  changeId: z.string().uuid().nullable(), policyRevision: TierRevisionSchema,
  committedAt: z.string().datetime(), enforcementState: state, statusUrl: z.string().nullable(),
});
export type TierPolicyUpdate = z.infer<typeof TierPolicyUpdateSchema>;
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const CohortExecutionScopeSchema = z.object({
  version: z.literal(1), migrationId: z.literal('live-paid-tier-active-node-cohort-v1'),
  activationScopeDigest: digest, cohortApprovalId: z.string().uuid(), deferredHistoryDigest: digest,
  nodeEnforcement: z.array(z.object({
    nodeId: z.number().int().positive(),
    approvalId: z.string().regex(/^[a-f0-9]{8}-[a-f0-9]{4}-5[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/),
    evidenceDigest: digest,
  }).strict()).refine(rows => rows.every((row, index) => index === 0 || row.nodeId > rows[index - 1].nodeId), '核定節點範圍重複或順序無效'),
}).strict();
export const TierPolicyStatusSchema = z.object({
  changeId: z.string().uuid(), tierId: z.number().int().positive(), policyRevision: TierRevisionSchema,
  state, committedAt: z.string().datetime(), discoveryComplete: z.boolean(),
  targets: z.object({ pending: z.number().int().nonnegative(), sent: z.number().int().nonnegative(), acked: z.number().int().nonnegative(), leaseExpired: z.number().int().nonnegative(), failed: z.number().int().nonnegative() }),
  lastErrorCode: z.string().nullable(), completedAt: z.string().nullable(),
  enforcementState: z.enum(['PENDING', 'APPLYING', 'APPLIED', 'FAILED', 'APPLIED_FOR_COHORT']).optional(),
  executionScope: CohortExecutionScopeSchema.optional(),
  deferredHistory: z.object({ manifestDigest: digest, status: z.literal('UNPROVED') }).strict().optional(),
}).refine(value => value.state !== 'APPLIED' || (value.discoveryComplete && value.targets.pending === 0 && value.targets.sent === 0 && value.targets.failed === 0), '節點套用證據尚未完整')
  .refine(value => {
    if (!value.executionScope && !value.deferredHistory && value.enforcementState !== 'APPLIED_FOR_COHORT') {
      return value.enforcementState === undefined || value.enforcementState === value.state;
    }
    return !!value.executionScope && !!value.deferredHistory &&
      value.executionScope.deferredHistoryDigest === value.deferredHistory.manifestDigest &&
      value.enforcementState === (value.state === 'APPLIED' ? 'APPLIED_FOR_COHORT' : value.state);
  }, '核定節點範圍或歷史驗證狀態不完整');
export type TierPolicyStatus = z.infer<typeof TierPolicyStatusSchema>;
export interface TierRevisionRecord { rowRevision?: string; policyRevision?: string }
export interface TierMutationResult extends TierRevisionRecord { id?: number; policyUpdate?: TierPolicyUpdate }
export interface TierSavedChange { tierId: number; brand: string; name: string; update?: TierPolicyUpdate }

export const MAX_AUTO_POLICY_CHECKS = 5;
export function tierPolicyChangeKey(change: TierSavedChange) {
  return `${change.brand}:${change.tierId}:${change.update?.changeId ?? 'unknown'}`;
}
/** Metadata acknowledgements have no obligation and must not replace one. */
export function retainTierPolicyChanges(current: TierSavedChange[], change: TierSavedChange) {
  if (change.tierId <= 0 || change.update?.changeId === null) return current;
  if (current.some(saved => tierPolicyChangeKey(saved) === tierPolicyChangeKey(change))) return current;
  return [...current, change];
}

export function tierExpectedRevisions(record: TierRevisionRecord) {
  const row = TierRevisionSchema.safeParse(record.rowRevision);
  const policy = TierRevisionSchema.safeParse(record.policyRevision);
  if (!row.success || !policy.success) throw new Error('資料缺少編輯版本，請重新整理；若仍無法取得，請先完成後台版本升級。');
  return { expectedRowRevision: row.data, expectedPolicyRevision: policy.data };
}

/** Retain the exact request on an ambiguous failure; changes get a fresh key. */
export class TierMutationSession {
  private previous: { identity: string; key: string } | null = null;
  constructor(private createKey: () => string = () => crypto.randomUUID()) {}
  options(path: string, method: string, brand: string, payload: Record<string, unknown>): RequestInit {
    if (!brand || brand === 'all') throw new Error('請明確指定目標品牌');
    const body = JSON.stringify({ ...payload, targetBrand: brand });
    const identity = JSON.stringify([path, method, brand, body]);
    if (this.previous?.identity !== identity) this.previous = { identity, key: this.createKey() };
    return { method, headers: { 'X-Target-Brand': brand, 'Idempotency-Key': this.previous.key }, body };
  }
  clear() { this.previous = null; }
}

export function tierPolicyStatusPath(tierId: number, brand: string, changeId: string) {
  if (!Number.isSafeInteger(tierId) || tierId < 1 || !brand || brand === 'all' || !z.string().uuid().safeParse(changeId).success) throw new Error('套用進度識別資料不完整');
  // Construct our own scoped route; never follow a server-supplied external URL.
  return `/api/admin/tiers/${tierId}/policy-updates/${changeId}?brand=${encodeURIComponent(brand)}`;
}
export function validateTierPolicyStatus(value: unknown, target: TierSavedChange): TierPolicyStatus {
  const result = TierPolicyStatusSchema.safeParse(value);
  if (!result.success) throw new Error('套用進度資料不完整，尚未確認完成，請再次查詢。');
  const parsed = result.data;
  if (parsed.tierId !== target.tierId || parsed.changeId !== target.update?.changeId || parsed.policyRevision !== target.update.policyRevision) throw new Error('套用進度與原始儲存請求不符');
  return parsed;
}
export function paidTierBlockedFields(values: { dailyTrafficMb?: number; sessionDurationMinutes?: number; requiresGrant?: boolean; isFree?: boolean }, references?: number) {
  if (!references || references <= 0) return [];
  return [values.dailyTrafficMb ? '每日流量' : null, values.sessionDurationMinutes ? '單次連線時長' : null, values.requiresGrant ? '兌換授權限制' : null, values.isFree ? '免費方案' : null].filter((v): v is string => v !== null);
}
export function tierMutationError(result: { msg?: string; errorCode?: string; data?: { errorCode?: string; code?: string } }) {
  const code = result.data?.errorCode ?? result.data?.code ?? result.errorCode ?? result.msg ?? '';
  if (/TIER_(ROW|POLICY)_REVISION_(CONFLICT|REQUIRED)/.test(code)) return '這份資料的版本已變更或不完整。已保留你的輸入，請重新載入最新資料後再編輯。';
  if (/TIER_PAID_POLICY_UNSUPPORTED/.test(code)) return '此等級已被付費權益引用，不能設定每日流量、單次連線時長、兌換授權或限速排程。請保留帳號期間配額，並修正不支援的欄位。';
  if (/TIER_IN_USE/.test(code)) return '此等級仍被權益、訂閱或未完成訂單引用，請先處理引用，不能直接刪除。';
  if (/IDEMPOTENCY_CONFLICT/.test(code)) return '原請求識別與內容不符，請保留目前輸入並重新載入資料後再確認。';
  return result.msg || '伺服器尚未確認結果，已保留原請求與輸入，請重試。';
}

export function parseTierMutationResult(data: unknown): TierMutationResult {
  if (!data || typeof data !== 'object') return {};
  const row = data as Record<string, unknown>;
  const parsed = TierPolicyUpdateSchema.safeParse(row.policyUpdate);
  return { id: typeof row.id === 'number' ? row.id : undefined, rowRevision: typeof row.rowRevision === 'string' ? row.rowRevision : undefined, policyRevision: typeof row.policyRevision === 'string' ? row.policyRevision : undefined, policyUpdate: parsed.success ? parsed.data : undefined };
}
